import React, { useMemo, useState } from "react";
import { 
  Paper, 
  Box, 
  IconButton, 
  Badge, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel, 
  useMediaQuery, 
  Toolbar,
  Card,
  Stack,
  Fade,
  Slide
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { 
  ShoppingCart as ShoppingCartIcon,
  CheckCircle,
  RadioButtonUnchecked
} from "@mui/icons-material";

import CreatorDetails from "./steps/creatordetails";
import CampaignDetails from "./steps/campaigndetails";
import Timelines from "./steps/campaigntimelines";
import CampaignConfirmation from "./steps/campaignconfirmation";
import LeftColumnSearch from "./leftColumnSearch";
import { indexToPromoFilter, platPromoToKey } from "../../../Utils/constants";
import { toast } from 'sonner';

// Steps for multi-creator flow
const steps = [
  { label: "Sourcing", description: "Find and select creators" },
  { label: "Creator Details", description: "Configure pricing & options" },
  { label: "Campaign Details", description: "Set up campaign info" },
  { label: "Timelines", description: "Plan delivery dates" },
  { label: "Confirmation", description: "Review & submit" },
];

function MarketplaceBooking({
  onInviteToPartnership,
  data,
  loading,
  onSubmitCampaign,
  prefilterEmail,
}) {
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const isLgScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));

  // Decide how many items per page in the left column
  let itemsPerPage = 4;
  if (isSmScreen) itemsPerPage = 6;
  if (isLgScreen) itemsPerPage = 12;
  if (isXlScreen) itemsPerPage = 16;

  // States for the multi-step flow
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [creatorsData, setCreatorsData] = useState([]);
  const [timelinesData, setTimelinesData] = useState({});
  const [campaignData, setCampaignData] = useState(null);
  const [promoIndex, setPromoIndex] = useState(1);
  const [isBlitzFeeOnCreators, setIsBlitzFeeOnCreators] = useState(true);

  // Example "idealDueDate" logic
  const today = new Date();
  const sevenDays = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  const idealDueDate = useMemo(
    () =>
      Object.entries(timelinesData).reduce((accum, [creator, timeline]) => {
        const latestDueDate = timeline.reduce(
          (innerAcc, event) =>
            event.projected_date <= innerAcc ? innerAcc : event.projected_date,
          sevenDays
        );
        return latestDueDate <= accum ? accum : latestDueDate;
      }, sevenDays),
    [timelinesData]
  );

  /**
   * Called when selecting/deselecting a creator in the LeftColumnSearch.
   */
  const handleAddToCampaign = (creatorId) => {
    const isSelected = selectedItems.has(creatorId);

    if (isSelected) {
      // If already selected, remove it
      setSelectedItems((prev) => (prev.delete(creatorId), new Set(prev)));
      setCreatorsData((prev) => prev.filter((item) => item.creator !== creatorId));
      setTimelinesData((prev) => {
        const updated = { ...prev };
        delete updated[creatorId];
        return updated;
      });
    } else {
      const { platform, promo: promotionType } = indexToPromoFilter(promoIndex);
      const creator = data.find((creator) => creator.creator == creatorId);
      const rateKey = promoIndex == -1 ? "" : platPromoToKey[platform][promotionType];
      toast.info('Adding creator to campaign', {
        description: `Creator ID: ${creatorId}, Rate: ${rateKey}`
      });
      const rate = promoIndex == -1 ? 0 : creator[platPromoToKey[platform][promotionType]];

      setSelectedItems((prev) => (prev.add(creatorId), new Set(prev)));
      setCreatorsData((prev) => [
        ...prev,
        {
          ...creator,
          videos: [
            {
              id: 1,
              rateKey: rateKey,
              crossPostingKeys: new Set([]),
            },
          ],
          basePrice: rate,
          usageRights: false,
          adjustedPrice: rate,
        },
      ]);
    }
  };

  /**
   * If the user explicitly removes a creator from the "Creator Details" step,
   * we just toggle them off as well.
   */
  const handleRemoveCreator = (creatorId) => {
    if (selectedItems.has(creatorId)) {
      handleAddToCampaign(creatorId); // effectively toggles it off
    }
  };

  /**
   * Called when user finishes the final step (Confirmation) in a multi-creator scenario
   */
  const handleFinish = () => {
    onSubmitCampaign({
      campaignData: {
        ...campaignData,
        idealDueDate,
      },
      timelinesData,
      creatorsData,
      isBlitzFeeOnCreators,
    });
  };

  /**
   * If user clicks the cart button:
   * - If at least one creator → go to step #1 (Creator Details)
   */
  const handleCartClick = () => {
    if (selectedItems.size > 0) {
      setActiveStep(1);
    }
  };

  const getStepIcon = (stepIndex) => {
    if (stepIndex < activeStep) {
      return <CheckCircle sx={{ color: 'success.main' }} />;
    }
    if (stepIndex === activeStep) {
      return <CheckCircle sx={{ color: 'primary.main' }} />;
    }
    return <RadioButtonUnchecked sx={{ color: 'grey.400' }} />;
  };

  return (
    <>
      {/* MODERN TOP NAVIGATION */}
      <Card
        elevation={0}
        square
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          background: (theme) => theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                {steps[activeStep].label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {steps[activeStep].description}
              </Typography>
            </Box>

            {/* Enhanced Cart Button */}
            <Box sx={{ position: 'relative' }}>
              <IconButton 
                onClick={handleCartClick}
                disabled={selectedItems.size === 0}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: selectedItems.size > 0 ? 'primary.main' : 'action.disabledBackground',
                  color: selectedItems.size > 0 ? 'white' : 'text.disabled',
                  boxShadow: selectedItems.size > 0 ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}` : 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: selectedItems.size > 0 ? 'primary.dark' : 'action.disabledBackground',
                    transform: selectedItems.size > 0 ? 'translateY(-2px)' : 'none',
                    boxShadow: selectedItems.size > 0 ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}` : 'none',
                  }
                }}
              >
                <Badge
                  badgeContent={selectedItems.size}
                  color="error"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.75rem',
                      minWidth: 20,
                      height: 20,
                      backgroundColor: '#ff4444',
                      animation: selectedItems.size > 0 ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)' }
                      }
                    }
                  }}
                >
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Box>
          </Stack>
        </Box>

        {/* MODERN DESKTOP STEPPER */}
        <Box sx={{ display: { xs: "none", md: "block" }, px: 3, pb: 3 }}>
          <Card 
            elevation={0}
            sx={{ 
              p: 3, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: 'background.default'
            }}
          >
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{
                '& .MuiStepConnector-root': {
                  top: 22,
                  '& .MuiStepConnector-line': {
                    borderTopWidth: 2,
                    borderColor: 'grey.300',
                  }
                },
                '& .MuiStepConnector-active .MuiStepConnector-line': {
                  borderColor: 'primary.main',
                },
                '& .MuiStepConnector-completed .MuiStepConnector-line': {
                  borderColor: 'success.main',
                }
              }}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => getStepIcon(index)}
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontWeight: index === activeStep ? 600 : 400,
                        color: index <= activeStep ? 'text.primary' : 'text.secondary',
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <Stack alignItems="center" spacing={0.5}>
                      <Typography 
                        variant="body2" 
                        fontWeight={index === activeStep ? 600 : 400}
                        color={index <= activeStep ? 'text.primary' : 'text.secondary'}
                      >
                        {step.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ display: { xs: 'none', lg: 'block' } }}
                      >
                        {step.description}
                      </Typography>
                    </Stack>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Card>
        </Box>

        {/* MOBILE PROGRESS INDICATOR */}
        <Box sx={{ display: { xs: "block", md: "none" }, px: 2, pb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: index <= activeStep ? 'primary.main' : 'grey.300',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Box>
      </Card>

      {/* MAIN CONTENT WITH TRANSITIONS */}
      <Box sx={{ 
        flex: 1, 
        bgcolor: 'background.default',
        minHeight: 'calc(100vh - 200px)'
      }}>
        {activeStep === 0 && (
          <Fade in timeout={500}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <LeftColumnSearch
                onCreatorSelect={handleAddToCampaign}
                selectedItems={selectedItems}
                data={data}
                loading={loading}
                itemsPerPage={itemsPerPage}
                onChangePromo={setPromoIndex}
              />
            </Box>
          </Fade>
        )}

        {activeStep === 1 && (
          <Slide direction="left" in timeout={400}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <CreatorDetails
                selectedCreators={selectedItems}
                creatorsData={creatorsData}
                setCreatorsData={setCreatorsData}
                isBlitzFeeOnCreators={isBlitzFeeOnCreators}
                setIsBlitzFeeOnCreators={setIsBlitzFeeOnCreators}
                onNext={() => setActiveStep(2)}
                onBack={() => setActiveStep(0)}
                onRemove={handleRemoveCreator}
                data={data}
              />
            </Box>
          </Slide>
        )}

        {activeStep === 2 && (
          <Slide direction="left" in timeout={400}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <CampaignDetails
                onNext={() => setActiveStep(3)}
                onBack={() => setActiveStep(1)}
                setCampaignData={setCampaignData}
                campaignData={campaignData}
                prefilterEmail={prefilterEmail}
              />
            </Box>
          </Slide>
        )}

        {activeStep === 3 && (
          <Slide direction="left" in timeout={400}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Timelines
                campaignDetails={campaignData}
                selectedCreatorsData={creatorsData}
                creatorTimelines={timelinesData}
                setCreatorTimelines={setTimelinesData}
                onNext={() => setActiveStep(4)}
                onBack={() => setActiveStep(2)}
              />
            </Box>
          </Slide>
        )}

        {activeStep === 4 && (
          <Slide direction="left" in timeout={400}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <CampaignConfirmation
                selectedCreatorsData={creatorsData}
                timelinesData={timelinesData}
                isBlitzFeeOnCreators={isBlitzFeeOnCreators}
                data={data}
                campaignData={campaignData}
                onBack={() => setActiveStep(3)}
                onFinish={handleFinish}
                idealDueDate={idealDueDate}
              />
            </Box>
          </Slide>
        )}
      </Box>

      {/* Bottom spacer for mobile */}
      <Toolbar sx={{ display: { xs: "block", md: "none" } }} />
    </>
  );
}

export default MarketplaceBooking;
